import React from 'react';
import { motion } from 'framer-motion';
import {
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  BanknotesIcon,
  CreditCardIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

interface FilterProps {
  dateRange: {
    start: string;
    end: string;
    preset: string;
  };
  departments: string[];
  metrics: string[];
  detailLevel: string;
  displayType: string;
  currency: string;
}

interface FinancialOverviewProps {
  filters: FilterProps;
}

const FinancialOverview: React.FC<FinancialOverviewProps> = ({ filters }) => {
  const { t, direction } = useLanguage();

  // Mock data for financial overview
  const kpiData = [
    {
      id: 'total_revenue',
      title: 'إجمالي الإيرادات',
      value: '2,847,500',
      currency: 'ر.س',
      change: '+12.5%',
      trend: 'up',
      icon: ArrowTrendingUpIcon,
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-50 dark:bg-green-900/20',
      description: 'مقارنة بالشهر الماضي'
    },
    {
      id: 'total_expenses',
      title: 'إجمالي المصروفات',
      value: '1,923,200',
      currency: 'ر.س',
      change: '+8.3%',
      trend: 'up',
      icon: ArrowTrendingDownIcon,
      color: 'from-red-500 to-red-600',
      bgColor: 'bg-red-50 dark:bg-red-900/20',
      description: 'مقارنة بالشهر الماضي'
    },
    {
      id: 'net_profit',
      title: 'صافي الربح',
      value: '924,300',
      currency: 'ر.س',
      change: '+18.7%',
      trend: 'up',
      icon: BanknotesIcon,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      description: 'مقارنة بالشهر الماضي'
    },
    {
      id: 'profit_margin',
      title: 'هامش الربح',
      value: '32.5',
      currency: '%',
      change: '+2.1%',
      trend: 'up',
      icon: ChartBarIcon,
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50 dark:bg-purple-900/20',
      description: 'مقارنة بالشهر الماضي'
    },
    {
      id: 'cash_flow',
      title: 'التدفق النقدي',
      value: '1,156,800',
      currency: 'ر.س',
      change: '+15.2%',
      trend: 'up',
      icon: CreditCardIcon,
      color: 'from-yellow-500 to-yellow-600',
      bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
      description: 'مقارنة بالشهر الماضي'
    },
    {
      id: 'roi',
      title: 'عائد الاستثمار',
      value: '24.8',
      currency: '%',
      change: '+3.4%',
      trend: 'up',
      icon: ChartBarIcon,
      color: 'from-indigo-500 to-indigo-600',
      bgColor: 'bg-indigo-50 dark:bg-indigo-900/20',
      description: 'مقارنة بالشهر الماضي'
    }
  ];

  // Revenue vs Expenses trend data
  const trendData = [
    { month: 'يناير', revenue: 2100000, expenses: 1800000, profit: 300000 },
    { month: 'فبراير', revenue: 2300000, expenses: 1850000, profit: 450000 },
    { month: 'مارس', revenue: 2500000, expenses: 1900000, profit: 600000 },
    { month: 'أبريل', revenue: 2400000, expenses: 1950000, profit: 450000 },
    { month: 'مايو', revenue: 2600000, expenses: 1920000, profit: 680000 },
    { month: 'يونيو', revenue: 2847500, expenses: 1923200, profit: 924300 }
  ];

  // Department performance data
  const departmentData = [
    { name: 'التسويق', revenue: 850000, expenses: 420000, profit: 430000, color: '#3B82F6' },
    { name: 'المبيعات', revenue: 1200000, expenses: 650000, profit: 550000, color: '#10B981' },
    { name: 'العمليات', revenue: 450000, expenses: 380000, profit: 70000, color: '#F59E0B' },
    { name: 'الموارد البشرية', revenue: 200000, expenses: 280000, profit: -80000, color: '#EF4444' },
    { name: 'تقنية المعلومات', revenue: 147500, expenses: 193200, profit: -45700, color: '#8B5CF6' }
  ];

  // Expense breakdown data
  const expenseData = [
    { name: 'الرواتب والأجور', value: 45, amount: 865440, color: '#3B82F6' },
    { name: 'التسويق والإعلان', value: 20, amount: 384640, color: '#10B981' },
    { name: 'المصروفات التشغيلية', value: 15, amount: 288480, color: '#F59E0B' },
    { name: 'الإيجارات والمرافق', value: 12, amount: 230784, color: '#EF4444' },
    { name: 'التقنية والبرمجيات', value: 8, amount: 153856, color: '#8B5CF6' }
  ];

  // Financial health indicators
  const healthIndicators = [
    {
      metric: 'السيولة السريعة',
      value: '1.8',
      status: 'excellent',
      benchmark: '> 1.5',
      description: 'قدرة ممتازة على تغطية الالتزامات قصيرة الأجل'
    },
    {
      metric: 'نسبة الدين إلى الأصول',
      value: '0.35',
      status: 'good',
      benchmark: '< 0.4',
      description: 'مستوى دين صحي ومقبول'
    },
    {
      metric: 'دوران المخزون',
      value: '6.2',
      status: 'warning',
      benchmark: '> 8.0',
      description: 'يحتاج تحسين في إدارة المخزون'
    },
    {
      metric: 'فترة تحصيل الذمم',
      value: '28 يوم',
      status: 'excellent',
      benchmark: '< 30 يوم',
      description: 'تحصيل ممتاز للمستحقات'
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'excellent':
        return 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-300';
      case 'good':
        return 'text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300';
      case 'warning':
        return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'critical':
        return 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-300';
      default:
        return 'text-gray-600 bg-gray-100 dark:bg-gray-900/30 dark:text-gray-300';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'excellent':
      case 'good':
        return <CheckCircleIcon className="w-4 h-4" />;
      case 'warning':
        return <ExclamationTriangleIcon className="w-4 h-4" />;
      case 'critical':
        return <ExclamationTriangleIcon className="w-4 h-4" />;
      default:
        return <ClockIcon className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {kpiData.map((kpi, index) => {
          const Icon = kpi.icon;
          return (
            <motion.div
              key={kpi.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`${kpi.bgColor} rounded-2xl p-6 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-all duration-300`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 rtl:space-x-reverse mb-2">
                    <div className={`w-10 h-10 bg-gradient-to-r ${kpi.color} rounded-lg p-2`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      {kpi.title}
                    </h3>
                  </div>
                  
                  <div className="flex items-baseline space-x-2 rtl:space-x-reverse mb-2">
                    <span className="text-2xl font-bold text-gray-900 dark:text-white">
                      {kpi.value.toLocaleString()}
                    </span>
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      {kpi.currency}
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-2 rtl:space-x-reverse">
                    <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                      kpi.trend === 'up' 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                        : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                    }`}>
                      {kpi.trend === 'up' ? (
                        <ArrowTrendingUpIcon className="w-3 h-3 ml-1 rtl:ml-0 rtl:mr-1" />
                      ) : (
                        <ArrowTrendingDownIcon className="w-3 h-3 ml-1 rtl:ml-0 rtl:mr-1" />
                      )}
                      {kpi.change}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {kpi.description}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue vs Expenses Trend */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            اتجاه الإيرادات والمصروفات
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis 
                dataKey="month" 
                className="text-xs"
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                className="text-xs"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => `${(value / 1000000).toFixed(1)}م`}
              />
              <Tooltip
                formatter={(value: any) => [`${(value / 1000).toLocaleString()} ألف ر.س`, '']}
                labelStyle={{ color: '#374151' }}
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
              <Area
                type="monotone"
                dataKey="revenue"
                stackId="1"
                stroke="#10B981"
                fill="#10B981"
                fillOpacity={0.6}
                name="الإيرادات"
              />
              <Area
                type="monotone"
                dataKey="expenses"
                stackId="2"
                stroke="#EF4444"
                fill="#EF4444"
                fillOpacity={0.6}
                name="المصروفات"
              />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Expense Breakdown Pie Chart */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            تفصيل المصروفات
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={expenseData}
                cx="50%"
                cy="50%"
                outerRadius={100}
                dataKey="value"
                label={({ name, value }) => `${name}: ${value}%`}
                labelLine={false}
              >
                {expenseData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: any, name: any) => [
                  `${value}% (${expenseData.find(d => d.name === name)?.amount.toLocaleString()} ر.س)`,
                  name
                ]}
              />
            </PieChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Department Performance */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          أداء الأقسام
        </h3>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={departmentData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis 
              dataKey="name" 
              className="text-xs"
              tick={{ fontSize: 12 }}
            />
            <YAxis 
              className="text-xs"
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => `${(value / 1000).toLocaleString()}ك`}
            />
            <Tooltip
              formatter={(value: any) => [`${(value / 1000).toLocaleString()} ألف ر.س`, '']}
              labelStyle={{ color: '#374151' }}
              contentStyle={{ 
                backgroundColor: '#fff', 
                border: '1px solid #e5e7eb',
                borderRadius: '8px'
              }}
            />
            <Bar dataKey="revenue" fill="#10B981" name="الإيرادات" radius={[4, 4, 0, 0]} />
            <Bar dataKey="expenses" fill="#EF4444" name="المصروفات" radius={[4, 4, 0, 0]} />
            <Bar dataKey="profit" fill="#3B82F6" name="الربح" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </motion.div>

      {/* Financial Health Indicators */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          مؤشرات الصحة المالية
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {healthIndicators.map((indicator, index) => (
            <motion.div
              key={indicator.metric}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg"
            >
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                  {indicator.metric}
                </h4>
                <div className={`p-1 rounded-full ${getStatusColor(indicator.status)}`}>
                  {getStatusIcon(indicator.status)}
                </div>
              </div>
              <div className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                {indicator.value}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                المعيار: {indicator.benchmark}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-300">
                {indicator.description}
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default FinancialOverview;
